import dayjs from "dayjs";

import { formatPhoneNumber } from "@/helpers/phone-helpers";
import { ICampaignLog } from "@/interfaces/campaign-logs.interface";
import { CampaignLogsMessages } from "@/messages/campaigns/logs/campaign-logs.messages";
import { CampaignLogStatus } from "@/types/campaign-log.types";

export type UseCampaignLogDataProps = {
  log: ICampaignLog;
  messages: CampaignLogsMessages;
};

export type UseCampaignLogDataReturn = {
  formattedDate: string;
  formattedStatus: string;
  formattedContactName: string;
  formattedPhone: string;
  formattedErrorMessage: string;
  formattedDetails: {
    formattedMessageDate: string;
    formattedMessageStatus: string;
    formattedErrorMessage: string | null;
  }[];
};

function useCampaignLogData({ log, messages }: UseCampaignLogDataProps): UseCampaignLogDataReturn {
  const formattedDate = log.scheduleDate ? dayjs(log.scheduleDate).format("DD/MM/YYYY HH:mm") : "-";

  const formattedStatus = (() => {
    switch (log.lastStatus) {
      case CampaignLogStatus.SENT:
        return messages.statusSent;
      case CampaignLogStatus.CLICKED:
        return messages.statusClicked;
      case CampaignLogStatus.FAILED:
        return messages.statusFailed;
      case CampaignLogStatus.READ:
        return messages.statusRead;
      case CampaignLogStatus.DELIVERED:
        return messages.statusDelivered;
      default:
        return messages.statusUnknown;
    }
  })();

  const formattedContactName = log.contactName || messages.unknownContactName;

  const formattedPhone = log.contactMobilePhone
    ? formatPhoneNumber(log.contactMobilePhone)
    : messages.phoneNotProvided;

  const formattedErrorMessage = log.lastErrorMessage || messages.noError;

  const formattedDetails =
    log.campaignLogDetail?.map((detail) => ({
      formattedMessageDate: detail.messageDate
        ? dayjs(detail.messageDate).format("DD/MM/YYYY HH:mm")
        : "-",
      formattedErrorMessage: detail.errorMessage || null,

      formattedMessageStatus: (() => {
        switch (detail.messageStatus) {
          case CampaignLogStatus.SENT:
            return messages.statusSent;
          case CampaignLogStatus.CLICKED:
            return messages.statusClicked;
          case CampaignLogStatus.FAILED:
            return messages.statusFailed;
          case CampaignLogStatus.DELIVERED:
            return messages.statusDelivered;
          case CampaignLogStatus.READ:
            return messages.statusRead;
          default:
            return messages.statusUnknown;
        }
      })(),
    })) || [];

  return {
    ...log,
    formattedDate,
    formattedStatus,
    formattedContactName,
    formattedPhone,
    formattedErrorMessage,
    formattedDetails,
  };
}

export default useCampaignLogData;
